import { Button, Card, Icon } from "@fanduel/og-fe-library";
import cx from "classnames";
import Link from "next/link";
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ErrorComponent from "@/components/empty/ErrorComponent";
import { RACING_PROJECTIONS_CTA_CLICKED } from "@/constants/amplitude";
import termsList from "@/constants/hr-terminology.json";
import { TermsT } from "@/types/horseRacing";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import styles from "./basicTerms.module.scss";

interface SearchEvent {
  preventDefault: () => void;
}

const BasicTerms = ({ initialTerms }: { initialTerms: TermsT }) => {
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const [terms, setTerms] = useState<TermsT>(initialTerms);
  const { logAmplitudeEvent } = useAmplitude();

  const search = useCallback(
    (e: SearchEvent) => {
      e.preventDefault();
      setSearchTerm(tempSearchTerm);
    },
    [tempSearchTerm]
  );

  const clearForm = () => {
    setSearchTerm("");
    setTempSearchTerm("");
  };

  useEffect(() => {
    startTransition(() => {
      if (searchTerm.length === 0) {
        setTerms(initialTerms);
      } else {
        setTerms(
          termsList.terminology
            .filter(
              (term) =>
                term.description
                  .toLocaleUpperCase()
                  .includes(searchTerm.toLocaleUpperCase()) ||
                term.term
                  .toLocaleUpperCase()
                  .includes(searchTerm.toLocaleUpperCase())
            )
            .slice(0, 3)
        );
      }
    });
  }, [searchTerm, initialTerms]);

  const [focused, setFocus] = useState(false);
  return (
    <Card variant="elevation" elevation="mid">
      <div>
        <h2 className={styles.title}>Horse Racing Basic Terms</h2>
      </div>
      <form onSubmit={search}>
        <div className={cx(styles.searchBox, focused && styles.focused)}>
          <Icon
            iconName="icons.search"
            iconSize={17}
            iconColor="colorBaseGreyDarken1"
          />
          <input
            ref={searchFieldRef}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onChange={(e) => setTempSearchTerm(e.target.value)}
            value={tempSearchTerm}
            type="text"
            placeholder="What are you looking for?"
          />
          <input
            type="reset"
            value="X"
            alt="Clear the search form"
            onClick={clearForm}
            disabled={tempSearchTerm.length === 0}
            data-testid="clearTerms"
          />
          <button
            data-testid="searchTerms"
            className={cx(focused && styles.focused)}
            onClick={search}
            type="button"
          >
            Search
          </button>
        </div>
      </form>
      <div className={styles.termsList}>
        {terms.length > 0 ? (
          terms.map((term) => (
            <div className={styles.termInfo} key={term.description}>
              <p className={styles.termName}>{term.term}</p>
              <p className={styles.termDesc}>{term.description}</p>
            </div>
          ))
        ) : (
          <div className={styles.errorContainer}>
            <ErrorComponent
              headline="No Terms Available"
              subtext="Please check back later."
              showCta={false}
            />
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Link href="/horse-racing/horse-betting-terms">
          <Button
            rightIconName="arrows.right"
            className={styles.footerButton}
            buttonStyle="Outlined"
            buttonColor="Blue"
            buttonText="Learn More Basic Terms"
            onClick={() => {
              logAmplitudeEvent(RACING_PROJECTIONS_CTA_CLICKED, {
                Module: "horse_racing_terms",
                "Link Text": "learn_more_basic_terms",
                "Link URL": "/horse-racing/horse-betting-terms",
              });
            }}
          />
        </Link>
      </div>
    </Card>
  );
};

export default BasicTerms;
